import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE = {
  name:      'JSDevBrains Digital',
  legalName: 'JSDevBrains Digital Private Limited',
  url:       'https://jsdevbrains.com',
  logo:      'https://jsdevbrains.com/logo.svg',
  ogImage:   'https://jsdevbrains.com/og-image.png',
  twitter:   '@jsdevbrains',
  description: 'JSDevBrains Digital Pvt Ltd — AI Agents, Cloud Consulting & Digital Transformation. Experts in .NET 8, Azure, Flutter, React & LangChain. Thanjavur, Tamil Nadu, India.',
  keywords: 'AI agents India, cloud consulting Thanjavur, digital transformation, .NET developer, Azure consultant, Flutter developer, React developer, LangChain, OpenAI GPT, JSDevBrains, DevBrains, BuyAndSell, NeighbourPro, Tamil Nadu tech company, AI startup India, software company Thanjavur',
};

const ORG_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': ['Organization', 'LocalBusiness', 'ProfessionalService'],
      '@id': 'https://jsdevbrains.com/#organization',
      name: 'JSDevBrains Digital Private Limited',
      alternateName: ['JSDevBrains', 'JSDevBrains Digital'],
      url: 'https://jsdevbrains.com',
      logo: { '@type': 'ImageObject', url: 'https://jsdevbrains.com/logo.svg', width: 512, height: 512 },
      image: 'https://jsdevbrains.com/og-image.png',
      description: 'AI Agents, Cloud Consulting, and Digital Transformation company based in Thanjavur, Tamil Nadu, India.',
      foundingDate: '2022',
      address: { '@type': 'PostalAddress', streetAddress: 'SR Complex 36/1c Pkt Road, Thiruchitrambalam', addressLocality: 'Thanjavur', addressRegion: 'Tamil Nadu', postalCode: '614628', addressCountry: 'IN' },
      geo: { '@type': 'GeoCoordinates', latitude: '10.7905', longitude: '79.1398' },
      contactPoint: [
        { '@type': 'ContactPoint', telephone: '+91-81236-29993', contactType: 'customer service', areaServed: ['IN','MY','SG','AE','GB','US'], availableLanguage: ['English','Tamil'] },
        { '@type': 'ContactPoint', telephone: '+91-81236-29993', contactType: 'sales', areaServed: ['IN','MY','SG','AE','GB','US'] },
      ],
      sameAs: ['https://linkedin.com/company/jsdevbrains','https://github.com/jsdevbrains','https://twitter.com/jsdevbrains'],
      priceRange: '₹₹₹',
      openingHoursSpecification: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:00', closes: '18:00' },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Technology Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'AI Agent Development' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Cloud Consulting (Azure)' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Digital Transformation' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mobile & Web App Development' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '.NET Backend Engineering' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'DevSecOps & CI/CD' } },
        ],
      },
    },
    {
      '@type': 'WebSite',
      '@id': 'https://jsdevbrains.com/#website',
      url: 'https://jsdevbrains.com',
      name: 'JSDevBrains Digital',
      publisher: { '@id': 'https://jsdevbrains.com/#organization' },
      potentialAction: { '@type': 'SearchAction', target: { '@type': 'EntryPoint', urlTemplate: 'https://jsdevbrains.com/?q={search_term_string}' }, 'query-input': 'required name=search_term_string' },
      inLanguage: 'en-IN',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',        item: 'https://jsdevbrains.com' },
        { '@type': 'ListItem', position: 2, name: 'Services',    item: 'https://jsdevbrains.com/#services' },
        { '@type': 'ListItem', position: 3, name: 'Products',    item: 'https://jsdevbrains.com/#products' },
        { '@type': 'ListItem', position: 4, name: 'Cloud',       item: 'https://jsdevbrains.com/#cloud' },
        { '@type': 'ListItem', position: 5, name: 'Internship',  item: 'https://jsdevbrains.com/#internship' },
        { '@type': 'ListItem', position: 6, name: 'Contact',     item: 'https://jsdevbrains.com/#contact' },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: 'What services does JSDevBrains offer?', acceptedAnswer: { '@type': 'Answer', text: 'JSDevBrains offers AI Agent Development (LangChain, OpenAI, RAG pipelines), Azure Cloud Consulting, Digital Transformation, Mobile & Web Apps (Flutter, React, Next.js), .NET 8 Backend, and DevSecOps/CI-CD services.' } },
        { '@type': 'Question', name: 'Where is JSDevBrains located?', acceptedAnswer: { '@type': 'Answer', text: 'JSDevBrains Digital Private Limited is based at SR Complex 36/1c Pkt Road, Thiruchitrambalam, Thanjavur, Tamil Nadu 614628, India. We serve clients globally.' } },
        { '@type': 'Question', name: 'How can I contact JSDevBrains?', acceptedAnswer: { '@type': 'Answer', text: 'Call or WhatsApp +91 81236 29993, or email hello@jsdevbrains.com. WhatsApp gets the fastest response — typically within minutes.' } },
        { '@type': 'Question', name: 'What is DevBrains?', acceptedAnswer: { '@type': 'Answer', text: 'DevBrains is JSDevBrains\'s AI-powered mentorship platform launching September 28, 2026, connecting developers with industry experts through intelligent matching and structured roadmaps.' } },
        { '@type': 'Question', name: 'Do you offer internships?', acceptedAnswer: { '@type': 'Answer', text: 'Yes! JSDevBrains offers 3–6 month internship programmes in AI/LangChain, Azure Cloud, Flutter, React, .NET, and Data/Power BI with real project experience and mentorship.' } },
        { '@type': 'Question', name: 'What countries does JSDevBrains serve?', acceptedAnswer: { '@type': 'Answer', text: 'JSDevBrains serves clients in India, Malaysia, Singapore, UAE, United Kingdom, and the United States, working fully remotely with enterprises worldwide.' } },
      ],
    },
  ],
};

export default function SEO({ title, description = SITE.description, url = SITE.url, image = SITE.ogImage, type = 'website', noindex = false }) {
  const pageTitle = title ? `${title} | ${SITE.name}` : `${SITE.name} | AI Agents · Cloud Consulting · Digital Transformation`;

  return (
    <Helmet>
      <html lang="en-IN" />
      <title>{pageTitle}</title>
      <meta name="description"     content={description} />
      <meta name="keywords"        content={SITE.keywords} />
      <meta name="author"          content={SITE.legalName} />
      <meta name="robots"          content={noindex ? 'noindex,nofollow' : 'index,follow,max-snippet:-1,max-image-preview:large'} />
      <meta name="googlebot"       content={noindex ? 'noindex' : 'index,follow'} />
      <link rel="canonical"        href={url} />

      <meta property="og:type"        content={type} />
      <meta property="og:url"         content={url} />
      <meta property="og:title"       content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image"       content={image} />
      <meta property="og:image:alt"   content="JSDevBrains — AI Agents & Cloud Technology" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name"   content={SITE.name} />
      <meta property="og:locale"      content="en_IN" />

      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={SITE.twitter} />
      <meta name="twitter:creator"     content={SITE.twitter} />
      <meta name="twitter:title"       content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={image} />

      <meta name="geo.region"    content="IN-TN" />
      <meta name="geo.placename" content="Thanjavur, Tamil Nadu, India" />
      <meta name="geo.position"  content="10.7905;79.1398" />
      <meta name="ICBM"          content="10.7905, 79.1398" />
      <meta name="rating"        content="general" />
      <meta name="language"      content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="apple-mobile-web-app-capable"          content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title"            content="JSDevBrains" />

      <script type="application/ld+json">{JSON.stringify(ORG_SCHEMA)}</script>
    </Helmet>
  );
}
